import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export function AboutVision() {
  return (
    <section className="bg-[var(--bg-base)] relative overflow-hidden" data-testid="vision-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[20%] left-0 w-full h-px bg-gradient-to-r from-transparent via-[var(--pi-violet)]/10 to-transparent" />
        <div className="absolute top-[55%] left-0 w-full h-px bg-gradient-to-r from-transparent via-[var(--pi-cyan)]/10 to-transparent" />
      </div>

      {/* Statement 1 */}
      <div className="relative py-28 md:py-36">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[var(--pi-violet)]/20 to-[var(--pi-cyan)]/20 border border-white/[0.06] flex items-center justify-center">
                <img src="/sendpi-icon.png" alt="" className="h-6 w-auto" />
              </div>
              <span className="text-white/25 text-xs font-space font-bold uppercase tracking-[0.3em]">The Vision</span>
            </div>
            <h2 className="font-space text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
              Every interaction deserves{' '}
              <span className="pi-gradient-text">precision.</span>
            </h2>
          </motion.div>
        </div>
      </div>

      {/* Statement 2 */}
      <div className="relative py-20 md:py-28 bg-[var(--bg-surface)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} transition={{ duration: 0.7 }}>
            <div>
              <p className="font-space text-3xl sm:text-4xl font-bold text-white leading-snug tracking-tight">
                Billions of messages.<br />
                <span className="text-white/30">Zero prediction.</span>
              </p>
            </div>
            <div className="space-y-5">
              <p className="text-white/45 text-base leading-relaxed">
                Every day, billions of messages are sent without any understanding of how they'll be received. A misread tone can lose a deal. A vague AI instruction can waste an hour.
              </p>
              <p className="text-white/45 text-base leading-relaxed">
                Grammar tools fix spelling. AI writes for you. But nothing tells you <span className="text-white/80 font-medium">how your words will actually land</span> — until now.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Statement 3 */}
      <div className="relative py-28 md:py-40">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--pi-violet)]/[0.03] rounded-full blur-[120px]" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full pi-gradient mb-10">
              <span className="font-space text-4xl font-bold text-white">&pi;</span>
            </div>
            <p className="font-space text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.15] tracking-tight">
              SendPI exists to make sure<br className="hidden sm:block" />
              you <span className="pi-gradient-text">never send blindly</span> again.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Two columns */}
      <div className="relative py-20 md:py-28 bg-[var(--bg-surface)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div className="grid md:grid-cols-2 gap-8 md:gap-12" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} transition={{ duration: 0.7 }}>
            <div className="border-l-2 border-[var(--pi-violet)]/40 pl-8 py-2">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-5 h-5 text-[var(--pi-violet)]" />
                <span className="text-[var(--pi-violet)] text-xs font-bold uppercase tracking-[0.2em]">People</span>
              </div>
              <p className="font-space text-xl font-bold text-white mb-3">Predict. Detect. Rewrite.</p>
              <p className="text-white/40 text-sm leading-relaxed">
                Predict emotional reactions before sending. Detect passive-aggression, guilt-tripping, and 15+ hidden signals. Rewrite with 10 tones and 30+ intents to land exactly the way you mean.
              </p>
            </div>
            <div className="border-l-2 border-[var(--pi-cyan)]/40 pl-8 py-2">
              <div className="flex items-center gap-3 mb-4">
                <Wand2 className="w-5 h-5 text-[var(--pi-cyan)]" />
                <span className="text-[var(--pi-cyan)] text-xs font-bold uppercase tracking-[0.2em]">Machines</span>
              </div>
              <p className="font-space text-xl font-bold text-white mb-3">Structure. Optimize. Deliver.</p>
              <p className="text-white/40 text-sm leading-relaxed">
                Transform rough ideas into precision casts for ChatGPT, Claude, Gemini, Copilot, and any AI — so you always get the best output.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative py-28 md:py-36">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[var(--pi-cyan)]/[0.03] rounded-full blur-[100px]" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div className="space-y-10" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} transition={{ duration: 0.7 }}>
            <p className="font-space text-2xl sm:text-3xl font-bold text-white/60 leading-snug tracking-tight">
              Clarity. Confidence. <span className="text-white">Intent.</span>
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/signup">
                <Button className="shimmer-btn text-white rounded-full px-10 py-6 text-base font-space font-bold border-0" data-testid="about-cta-signup">
                  Start using SendPI <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="/#try-now">
                <Button variant="outline" className="border-white/10 text-white/60 hover:bg-white/5 rounded-full px-10 py-6 text-base font-space" data-testid="about-cta-try">
                  Try it now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
